// The quote drawer's payment writes (#1559): Send Square link, the pending
// e-transfer (Approach §6), deleting a payment and the balance-due date. Each
// toasts its own outcome and resolves to whether it worked.
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { QuoteRow } from "@/hooks/useQuotes";
import { invalidateEventViews } from "@/hooks/useEventsPipeline";
import { captureError, getErrorMessage } from "@/lib/error-message";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import { firstName, formatMoney, paymentLinkPercent } from "./drawerLogic";
import { useBusy } from "./useQuoteActions";

const refresh = (qc: QueryClient, quoteId: string) => {
  qc.invalidateQueries({ queryKey: ["quote_payments", quoteId] });
  qc.invalidateQueries({ queryKey: ["quotes"] });
  invalidateEventViews(qc);
};

const today = () => new Date().toISOString().split("T")[0];

/** Clipboard write that reports failure instead of throwing (iOS Safari, insecure contexts). */
async function copied(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export interface PaymentLinkResult {
  url: string;
  /** False when the clipboard refused: the panel shows the URL to copy by hand. */
  copied: boolean;
}

/** Send Square link: makes the link and copies it. Nothing is sent to the client. */
export function useSquarePaymentLink(quote: QuoteRow) {
  const qc = useQueryClient();
  const { busy, run } = useBusy();
  const total = Number(quote.total) || 0;
  const send = (amount: number) =>
    run<PaymentLinkResult | null>(null, async () => {
      try {
        const { data, error } = await supabase.functions.invoke("square-payments", {
          body: { action: "get-payment-link", quote_id: quote.id, deposit_percent: paymentLinkPercent(amount, total) },
        });
        if (error) throw error;
        const url = (data as { payment_link?: string } | null)?.payment_link;
        if (!url) throw new Error("Square didn't return a payment link");
        qc.invalidateQueries({ queryKey: ["quotes"] });
        const ok = await copied(url);
        if (ok) toast.success(`Square link for ${formatMoney(amount)} copied. Paste it in a text or email to ${firstName(quote.client_name)}.`);
        return { url, copied: ok };
      } catch (err) {
        const msg = await edgeFunctionErrorMessage(err, "Couldn't create the payment link");
        captureError(err, "quote_payment_link", msg);
        toast.error(msg);
        return null;
      }
    });
  return { send, sending: busy };
}

/** Waiting on e-transfer: mark-etransfer, then store the amount if none is pending. */
export function useWaitOnEtransfer(quote: QuoteRow) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { busy, run } = useBusy();
  const wait = (amount: number) =>
    run(false, async () => {
      if (!(amount > 0)) return false;
      const { error: fnError } = await supabase.functions.invoke("square-payments", {
        body: { action: "mark-etransfer", quote_id: quote.id },
      });
      if (fnError) {
        const msg = await edgeFunctionErrorMessage(fnError, "Couldn't mark the quote for e-transfer");
        captureError(fnError, "quote_mark_etransfer", msg);
        toast.error(msg);
        return false;
      }
      // Guarded so one tab can't overwrite another's pending amount.
      const { data, error } = await supabase
        .from("quotes")
        .update({ pending_etransfer_amount: amount, pending_etransfer_requested_at: new Date().toISOString(), updated_by: user?.id })
        .eq("id", quote.id)
        .is("pending_etransfer_amount", null)
        .select("id");
      qc.invalidateQueries({ queryKey: ["quotes"] });
      invalidateEventViews(qc);
      if (error) toast.error(getErrorMessage(error, "Couldn't store the pending e-transfer"));
      else if (!data?.length) toast.error("There's already an e-transfer pending on this quote.");
      else toast.success("Marked as waiting on an e-transfer. It counts once you mark it received.");
      return !error && !!data?.length;
    });
  return { wait, waiting: busy };
}

type Pending = { quoteId: string; amount: number; requestedAt: string; userId: string | null };

/** Clear the pending columns, but only for the request this drawer showed. */
function clearPending({ quoteId, requestedAt, userId }: Pending) {
  return supabase
    .from("quotes")
    .update({ pending_etransfer_amount: null, pending_etransfer_requested_at: null, updated_by: userId })
    .eq("id", quoteId)
    .eq("pending_etransfer_requested_at", requestedAt)
    .select("id");
}

/**
 * After a failed insert: was the row written anyway (a lost response)? Found
 * means recorded; a confirmed miss puts the pending columns back; an unreadable
 * answer restores nothing, so the worst case is a pending row to re-add, never
 * a payment counted twice.
 */
async function settleFailedInsert(p: Pending, notes: string, insertError: unknown): Promise<boolean> {
  const { data, error } = await supabase.from("quote_payments").select("id").eq("quote_id", p.quoteId).eq("notes", notes);
  if (error) {
    toast.error("Couldn't confirm the e-transfer was recorded. Reload and check Payments before recording it.");
    return false;
  }
  if (data?.length) return true;
  const restore = await supabase
    .from("quotes")
    .update({ pending_etransfer_amount: p.amount, pending_etransfer_requested_at: p.requestedAt, updated_by: p.userId })
    .eq("id", p.quoteId)
    .is("pending_etransfer_amount", null);
  toast.error(getErrorMessage(insertError, "Couldn't record the e-transfer"));
  // Nothing was counted either way; staff can add the pending row again.
  if (restore.error) toast.error("Couldn't put the pending e-transfer back. Add it again from Collect payment.");
  return false;
}

/** Claim the pending row, insert one payment, then confirm (Approach §6). */
async function receivePending(p: Pending): Promise<boolean> {
  const claim = await clearPending(p);
  if (claim.error) {
    toast.error(getErrorMessage(claim.error, "Couldn't mark it received"));
    return false;
  }
  if (!claim.data?.length) {
    toast.error("Already marked received.");
    return false;
  }
  // The marker the read-back looks for.
  const notes = `Pending e-transfer asked ${p.requestedAt}`;
  const { error } = await supabase.from("quote_payments").insert({
    quote_id: p.quoteId,
    amount: p.amount,
    fee_amount: 0,
    method: "etransfer",
    reference: "E-transfer received",
    notes,
    payment_date: today(),
    recorded_by: p.userId,
  });
  return error ? settleFailedInsert(p, notes, error) : true;
}

const pendingOf = (quote: QuoteRow, userId: string | null): Pending | null =>
  quote.pending_etransfer_requested_at && Number(quote.pending_etransfer_amount) > 0
    ? { quoteId: quote.id, amount: Number(quote.pending_etransfer_amount), requestedAt: quote.pending_etransfer_requested_at, userId }
    : null;

/** Mark received on the pending row: at most one quote_payments row per request. */
export function useMarkEtransferReceived(quote: QuoteRow) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { busy, run } = useBusy();
  const receive = () =>
    run(false, async () => {
      const p = pendingOf(quote, user?.id ?? null);
      if (!p) return false;
      const ok = await receivePending(p);
      refresh(qc, quote.id);
      if (ok) toast.success("Marked received.");
      return ok;
    });
  return { receive, receiving: busy };
}

/** × on the pending row: stop waiting. Nothing was recorded, so nothing is undone. */
export function useStopWaitingEtransfer(quote: QuoteRow) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { busy, run } = useBusy();
  const stop = () =>
    run(false, async () => {
      const p = pendingOf(quote, user?.id ?? null);
      if (!p) return false;
      const { data, error } = await clearPending(p);
      qc.invalidateQueries({ queryKey: ["quotes"] });
      invalidateEventViews(qc);
      if (error) toast.error(getErrorMessage(error, "Couldn't remove the pending e-transfer"));
      else if (!data?.length) toast.error("That e-transfer was already cleared.");
      else toast.success("Stopped waiting for the e-transfer.");
      return !error && !!data?.length;
    });
  return { stop, stopping: busy };
}

/** × then Delete on a payment row: the old panel's handleDeletePayment. */
export function useDeleteQuotePayment(quoteId: string) {
  const qc = useQueryClient();
  const { busy, run } = useBusy();
  const remove = (paymentId: string) =>
    run(false, async () => {
      const { error } = await supabase.from("quote_payments").delete().eq("id", paymentId);
      if (error) {
        const msg = getErrorMessage(error, "Failed to remove payment");
        captureError(error, "quote_payment_delete", msg);
        toast.error(msg);
        return false;
      }
      refresh(qc, quoteId);
      toast.success("Payment deleted.");
      return true;
    });
  return { remove, removing: busy };
}

const FULL_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** A real calendar date typed in full; a partial or impossible value is not. */
export function isFullDate(value: string): boolean {
  if (!FULL_DATE.test(value)) return false;
  const d = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().startsWith(value);
}

/**
 * Balance due date: writes only a full yyyy-mm-dd; anything else writes
 * nothing. No double-click guard: a second pick is a new date, not a repeat.
 */
export function useSetPaymentDueDate(quoteId: string) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const setDueDate = async (value: string): Promise<boolean> => {
    if (!isFullDate(value)) return false;
    const { error } = await supabase.from("quotes").update({ payment_due_date: value, updated_by: user?.id }).eq("id", quoteId);
    if (error) {
      toast.error(getErrorMessage(error, "Couldn't save the due date"));
      return false;
    }
    qc.invalidateQueries({ queryKey: ["quotes"] });
    invalidateEventViews(qc);
    return true;
  };
  return { setDueDate };
}
